import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Alert, Typography, Divider } from 'antd';
import { MailOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

function Login() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login, restoreSavedState } = useAuth();
    const navigate = useNavigate();

    const onFinish = async (values) => {
        setLoading(true);
        setError('');
        try {
            const result = await login(values.email, values.password);
            if (result.success) {
                // Try to restore saved state/returnUrl first
                const saved = restoreSavedState();
                if (saved?.returnUrl) {
                    navigate(saved.returnUrl, { replace: true });
                    return;
                }
                const { user_type } = result.user;
                if (user_type === 'admin') {
                    navigate('/admin/dashboard');
                } else if (user_type === 'vendor') {
                    navigate('/vendor/dashboard');
                } else {
                    navigate('/dashboard');
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] py-8 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-md">
                {/* Header Section */}
                <div className="text-center mb-8">
                    {/* Logo */}
                    <div className="mb-6 flex items-center justify-center gap-2">
                        <div className="w-8 h-8 bg-[#C97D60] rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                            </svg>
                        </div>
                        <h1 className="text-4xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>
                            Spoon & Spice
                        </h1>
                    </div>
                    <p className="text-base text-[#4A4A4A] mb-6">Welcome back to your culinary journey</p>
                    
                    {/* Heading with icon */}
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-[#C97D60] rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                            </svg>
                        </div>
                        <Title level={2} className="!mb-0 !text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>Sign in to your account</Title>
                    </div>
                    <Paragraph className="text-[#4A4A4A] mb-0">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-[#C97D60] hover:text-[#B8735A] font-medium transition-colors">
                            Create one here
                        </Link>
                    </Paragraph>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="px-8 py-8">
                        {error && (
                            <Alert 
                                type="error" 
                                message={error} 
                                showIcon 
                                className="mb-6 rounded-lg"
                            />
                        )}

                        <Form
                            layout="vertical"
                            onFinish={onFinish}
                            className="space-y-6"
                            size="large"
                        >
                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Email Address</span>}
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter your email address' },
                                    { type: 'email', message: 'Please enter a valid email address' },
                                ]}
                                className="mb-6"
                            >
                                <Input 
                                    placeholder="Enter your email address" 
                                    autoComplete="email"
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={<MailOutlined className="text-[#4A4A4A]" />}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Password</span>}
                                name="password"
                                rules={[{ required: true, message: 'Please enter your password' }]}
                                className="mb-6"
                            >
                                <Input.Password 
                                    placeholder="Enter your password" 
                                    autoComplete="current-password"
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            <div className="flex items-center justify-between mb-6">
                                <Form.Item name="remember" valuePropName="checked" noStyle>
                                    <Checkbox className="text-gray-600">
                                        Remember me
                                    </Checkbox>
                                </Form.Item>
                                <Link 
                                    to="/forgot-password" 
                                    className="text-[#C97D60] hover:text-[#B8735A] text-sm font-medium transition-colors"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            <Form.Item className="mb-6">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={loading} 
                                    block
                                    className="h-12 rounded-lg bg-[#C97D60] border-0 font-semibold text-base hover:bg-[#B8735A] transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default Login;

